import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiClient } from 'lib/api/client';
import { ListingBookmarkTypeEnum, States } from 'lib/api/generated';
import { QUERY_KEYS } from 'settings/config';

type Params = {
  page?: number;
  pageSize?: number;
  sold?: string;
  auction?: string;
  product?: 'CT' | 'GT' | 'SH';
  state?: States;
  search?: string;
  quantityMax?: string;
  quantityMin?: string;
  priceMax?: string;
  priceMin?: string;
  priceType?: string;
  type?: 'BUY' | 'SELL';
  stockCat?: string;
  networks?: string;
  apiKey: boolean;
  bookmark?: ListingBookmarkTypeEnum;
  org?: string;
  enabled?: boolean;
  aa_source?: string;
};

export type MarketplacePublicListingKeyParams = Pick<
  Params,
  | 'page'
  | 'pageSize'
  | 'sold'
  | 'auction'
  | 'product'
  | 'state'
  | 'search'
  | 'priceType'
  | 'priceMax'
  | 'priceMin'
  | 'quantityMax'
  | 'quantityMin'
  | 'type'
  | 'stockCat'
  | 'networks'
  | 'apiKey'
  | 'bookmark'
  | 'org'
>;

/**
 * Builds the TanStack Query key for the public listings infinite query.
 * Exported so server-side prefetching (e.g. marketplace index SSR) can produce
 * an identical key and the dehydrated cache is picked up on hydration.
 */
export const getMarketplacePublicListingQueryKey = ({
  page,
  pageSize,
  sold,
  auction,
  product,
  state,
  search,
  priceMax,
  priceMin,
  quantityMax,
  quantityMin,
  priceType,
  type,
  stockCat,
  networks,
  apiKey,
  bookmark,
  org,
}: MarketplacePublicListingKeyParams) => [
  QUERY_KEYS.marketplacePublicListing,
  page,
  pageSize,
  sold,
  auction,
  product,
  state,
  search,
  priceType,
  priceMax,
  priceMin,
  quantityMax,
  quantityMin,
  type,
  stockCat,
  networks,
  apiKey,
  bookmark,
  org,
];

export function useMarketplacePublicListing({
  page,
  pageSize,
  sold,
  auction,
  product,
  state,
  search,
  priceType,
  priceMax,
  priceMin,
  quantityMax,
  quantityMin,
  type,
  stockCat,
  networks,
  apiKey,
  bookmark,
  org,
  enabled = true,
  aa_source,
}: Params) {
  return useInfiniteQuery(
    getMarketplacePublicListingQueryKey({
      page,
      pageSize,
      sold,
      auction,
      product,
      state,
      search,
      priceType,
      priceMax,
      priceMin,
      quantityMax,
      quantityMin,
      type,
      stockCat,
      networks,
      apiKey,
      bookmark,
      org,
    }),
    ({ pageParam }) => {
      return new ApiClient({
        aa_source,
      }).marketplacePublicListingApi.marketplaceListingList({
        pageSize: pageSize || 20,
        page: pageParam || 1,
        sold: sold !== undefined ? sold === 'true' : undefined,
        auction: auction !== undefined ? auction === 'true' : undefined,
        product,
        state,
        search,
        priceType,
        priceMax,
        priceMin,
        quantityMax,
        quantityMin,
        type,
        stockCat,
        network: networks,
        bookmark: bookmark,
        org,
      });
    },
    {
      enabled,
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => page.data),
      }),
      getNextPageParam: (lastPage) => lastPage.data.next,
      getPreviousPageParam: (lastPage) => lastPage.data.previous,
      keepPreviousData: true,
    }
  );
}
